'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';

import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Eye,
  Edit3,
  Flag,
  Download,
  Plus,
  X,
  CheckSquare,
  Square,
  AlertTriangle,
  Building2,
  RefreshCw,
  ChevronRight,
  BarChart2,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { type RiskTier, type ContractStatus, type DDStatus } from '@/lib/mockData';
import { vendorService, type DbVendor } from '@/lib/supabase/tprmService';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';
import VendorDetailModal from './VendorDetailModal';
import { useRole } from '@/contexts/RoleContext';
import { exportCSV, exportPDF } from '@/lib/exportUtils';

type SortKey = keyof DbVendor | null;
type SortDir = 'asc' | 'desc';

const RISK_TIERS: RiskTier[] = ['Critical', 'High', 'Moderate', 'Low'];
const CONTRACT_STATUSES: ContractStatus[] = ['Active', 'Expiring', 'Draft', 'Legal Review', 'Pending Signature', 'Terminated', 'Archived'];
const DD_STATUSES: DDStatus[] = ['Complete', 'In Progress', 'Pending Approval', 'Overdue', 'Not Started', 'Waived'];

const VENDOR_EXPORT_COLUMNS = [
  { header: 'Vendor ID', key: 'vendor_id' },
  { header: 'Name', key: 'name' },
  { header: 'Category', key: 'category' },
  { header: 'Department', key: 'department' },
  { header: 'Risk Tier', key: 'risk_tier' },
  { header: 'Contract Status', key: 'contract_status' },
  { header: 'DD Status', key: 'dd_status' },
  { header: 'Contract End', key: 'contract_end' },
  { header: 'Open Issues', key: 'open_issues' },
  { header: 'Overall Status', key: 'overall_status' },
];

// Map DB snake_case vendor to the shape VendorDetailModal expects (camelCase Vendor)
function dbToVendor(v: DbVendor) {
  return {
    id: v.id,
    name: v.name,
    dba: v.dba,
    vendorId: v.vendor_id,
    category: v.category,
    services: v.services,
    department: v.department,
    departmentOwner: v.department_owner,
    contractOwner: v.contract_owner,
    riskTier: v.risk_tier,
    criticality: v.criticality,
    lifecycleStage: v.lifecycle_stage,
    contractStatus: v.contract_status,
    ddStatus: v.dd_status,
    insuranceStatus: v.insurance_status,
    contractStart: v.contract_start || '',
    contractEnd: v.contract_end || '',
    nextReview: v.next_review || '',
    lastReview: v.last_review || '',
    residualRisk: v.residual_risk,
    dataHandled: v.data_handled || [],
    systemsAccessed: v.systems_accessed || [],
    hostingModel: v.hosting_model,
    openIssues: v.open_issues,
    openCriticalIssues: v.open_critical_issues,
    overallStatus: v.overall_status,
  };
}

export default function VendorInventory() {
  const searchParams = useSearchParams();
  const [vendors, setVendors] = useState<DbVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRisk, setFilterRisk] = useState<RiskTier | ''>((searchParams.get('risk') as RiskTier) || '');
  const [filterContract, setFilterContract] = useState<ContractStatus | ''>('');
  const [filterDD, setFilterDD] = useState<DDStatus | ''>('');
  const [filterDept, setFilterDept] = useState(searchParams.get('dept') || '');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [detailVendor, setDetailVendor] = useState<DbVendor | null>(null);
  const { can } = useRole();

  // Detect if we arrived from Analytics (has risk or dept param)
  const fromAnalytics = !!(searchParams.get('risk') || searchParams.get('dept'));
  const analyticsRisk = searchParams.get('risk') || '';
  const analyticsDept = searchParams.get('dept') || '';

  // Load vendors from Supabase
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    vendorService.getAll().then((data) => {
      if (!cancelled) {
        setVendors(data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    // Real-time subscription
    const unsubscribe = vendorService.subscribeToAll((updated) => {
      if (!cancelled) setVendors(updated);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const DEPARTMENTS = useMemo(() => [...new Set(vendors.map((v) => v.department))].sort(), [vendors]);

  const filtered = useMemo(() => {
    let result = vendors.filter((v) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        v.name.toLowerCase().includes(q) ||
        v.vendor_id.toLowerCase().includes(q) ||
        v.category.toLowerCase().includes(q) ||
        v.department.toLowerCase().includes(q);
      const matchRisk = !filterRisk || v.risk_tier === filterRisk;
      const matchContract = !filterContract || v.contract_status === filterContract;
      const matchDD = !filterDD || v.dd_status === filterDD;
      const matchDept = !filterDept || v.department === filterDept;
      return matchSearch && matchRisk && matchContract && matchDD && matchDept;
    });

    if (sortKey) {
      result = [...result].sort((a, b) => {
        const av = a[sortKey as keyof DbVendor] ?? '';
        const bv = b[sortKey as keyof DbVendor] ?? '';
        const cmp = String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [vendors, search, filterRisk, filterContract, filterDD, filterDept, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey]
  );

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginated.length && paginated.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginated.map((v) => v.id)));
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFilterRisk('');
    setFilterContract('');
    setFilterDD('');
    setFilterDept('');
    setPage(1);
  };

  const hasFilters = !!(search || filterRisk || filterContract || filterDD || filterDept);

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col)
      return <ChevronsUpDown size={11} className="text-[hsl(var(--text-muted))] opacity-50" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={11} className="text-[hsl(var(--accent))]" />
    ) : (
      <ChevronDown size={11} className="text-[hsl(var(--accent))]" />
    );
  };

  const thCls =
    'text-left px-3 py-2 font-medium text-[hsl(var(--text-muted))] whitespace-nowrap select-none cursor-pointer hover:text-[hsl(var(--text-primary))] transition-colors text-xs';

  const handleExportCSV = () => {
    const rows = filtered.map((v) => ({ ...v } as unknown as Record<string, unknown>));
    exportCSV(VENDOR_EXPORT_COLUMNS, rows, `TPRM-Vendors-${new Date().toISOString().slice(0, 10)}`);
  };

  const handleExportPDF = () => {
    const rows = filtered.map((v) => ({ ...v } as unknown as Record<string, unknown>));
    exportPDF(
      'Vendor Management Report',
      `City of Pleasanton TPRM Registry · ${filtered.length} vendors`,
      VENDOR_EXPORT_COLUMNS,
      rows
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full min-h-0 items-center justify-center gap-3">
        <RefreshCw size={22} className="animate-spin text-[hsl(var(--accent))]" />
        <p className="text-sm text-[hsl(var(--text-muted))]">Loading vendor registry…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Breadcrumb — shown when arriving from Analytics */}
      {fromAnalytics && (
        <div className="px-5 py-2 bg-[hsl(var(--surface-raised))] border-b border-[hsl(var(--border))] flex items-center gap-1.5 text-xs flex-shrink-0">
          <BarChart2 size={12} className="text-[hsl(var(--accent))]" />
          <Link href="/analytics" className="text-[hsl(var(--accent))] hover:underline font-medium">
            Analytics
          </Link>
          <ChevronRight size={11} className="text-[hsl(var(--text-muted))]" />
          <span className="text-[hsl(var(--text-muted))]">Vendor Management</span>
          {(analyticsRisk || analyticsDept) && (
            <>
              <ChevronRight size={11} className="text-[hsl(var(--text-muted))]" />
              <span className="text-[hsl(var(--text-primary))] font-medium">
                {[analyticsRisk && `Risk: ${analyticsRisk}`, analyticsDept && `Dept: ${analyticsDept}`].filter(Boolean).join(' · ')}
              </span>
            </>
          )}
          <button
            onClick={clearFilters}
            className="ml-auto flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 px-2 py-0.5 rounded-md transition-colors"
          >
            <X size={11} /> Clear Filters
          </button>
        </div>
      )}

      {/* Page header */}
      <div className="px-5 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-center justify-between gap-3 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--text-primary))]">Vendor Management</h1>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">
            {filtered.length} of {vendors.length} vendors · City of Pleasanton TPRM Registry
          </p>
        </div>
        <div className="flex items-center gap-2">
          {can('vendor:export') && (
          <button
            onClick={handleExportCSV}
            className="btn-secondary"
          >
            <Download size={13} />
            Export CSV
          </button>
          )}
          {can('vendor:export') && (
          <button
            onClick={handleExportPDF}
            className="btn-secondary flex items-center gap-1.5"
            title="Export vendor list as PDF"
          >
            <FileText size={13} />
            Export PDF
          </button>
          )}
          {can('vendor:create') && (
          <button
            onClick={() =>
              toast.info('Intake wizard', {
                description: 'New vendor intake workflow — navigate to Intake Queue.',
              })
            }
            className="btn-primary"
          >
            <Plus size={13} />
            New Vendor
          </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="px-5 py-2.5 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-center gap-2 flex-wrap flex-shrink-0">
        <div className="relative flex-1 min-w-48 max-w-72">
          <Search
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search vendors, IDs, categories…"
            className="input-base pl-8 text-xs h-8"
          />
        </div>

        <select
          value={filterRisk}
          onChange={(e) => {
            setFilterRisk(e.target.value as RiskTier | '');
            setPage(1);
          }}
          className="input-base text-xs h-8 w-auto cursor-pointer"
        >
          <option value="">All Risk Tiers</option>
          {RISK_TIERS.map((r) => (
            <option key={`rf-${r}`} value={r}>
              {r}
            </option>
          ))}
        </select>

        <select
          value={filterContract}
          onChange={(e) => {
            setFilterContract(e.target.value as ContractStatus | '');
            setPage(1);
          }}
          className="input-base text-xs h-8 w-auto cursor-pointer"
        >
          <option value="">All Contract Statuses</option>
          {CONTRACT_STATUSES.map((s) => (
            <option key={`cs-${s}`} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={filterDD}
          onChange={(e) => {
            setFilterDD(e.target.value as DDStatus | '');
            setPage(1);
          }}
          className="input-base text-xs h-8 w-auto cursor-pointer"
        >
          <option value="">All DD Statuses</option>
          {DD_STATUSES.map((s) => (
            <option key={`dd-${s}`} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={filterDept}
          onChange={(e) => {
            setFilterDept(e.target.value);
            setPage(1);
          }}
          className="input-base text-xs h-8 w-auto cursor-pointer"
        >
          <option value="">All Departments</option>
          {DEPARTMENTS.map((d) => (
            <option key={`dept-${d}`} value={d}>
              {d}
            </option>
          ))}
        </select>

        {hasFilters && (
          <button onClick={clearFilters} className="btn-ghost text-xs h-8 gap-1">
            <X size={11} />
            Clear
          </button>
        )}

        <div className="ml-auto text-xs text-[hsl(var(--text-muted))] tabular-nums">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && can('vendor:bulkAction') && (
        <div className="px-5 py-2 bg-teal-50 dark:bg-teal-950/30 border-b border-teal-200 dark:border-teal-900 flex items-center gap-3 flex-shrink-0 animate-slide-up">
          <span className="text-xs font-medium text-teal-700 dark:text-teal-400">
            {selectedIds.size} vendor{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => {
              toast.success(`${selectedIds.size} vendors exported`);
              setSelectedIds(new Set());
            }}
            className="btn-secondary text-xs h-7"
          >
            Export Selected
          </button>
          <button
            onClick={() => {
              toast.info(`${selectedIds.size} vendors flagged for review`);
              setSelectedIds(new Set());
            }}
            className="btn-secondary text-xs h-7"
          >
            <Flag size={11} /> Flag for Review
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="btn-ghost text-xs h-7 ml-auto"
          >
            <X size={11} /> Deselect All
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs min-w-[1200px]">
          <thead className="sticky top-0 z-10 bg-[hsl(var(--background))] border-b border-[hsl(var(--border))]">
            <tr>
              <th className="px-3 py-2 w-8">
                <button
                  onClick={toggleSelectAll}
                  className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
                >
                  {selectedIds.size === paginated.length && paginated.length > 0 ? (
                    <CheckSquare size={14} className="text-teal-600" />
                  ) : (
                    <Square size={14} />
                  )}
                </button>
              </th>
              <th className={thCls} onClick={() => handleSort('name')}>
                <span className="flex items-center gap-1">
                  Vendor <SortIcon col="name" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('vendor_id')}>
                <span className="flex items-center gap-1">
                  Vendor ID <SortIcon col="vendor_id" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('category')}>
                <span className="flex items-center gap-1">
                  Category <SortIcon col="category" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('department')}>
                <span className="flex items-center gap-1">
                  Department <SortIcon col="department" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('risk_tier')}>
                <span className="flex items-center gap-1">
                  Risk Tier <SortIcon col="risk_tier" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('criticality')}>
                <span className="flex items-center gap-1">
                  Criticality <SortIcon col="criticality" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('lifecycle_stage')}>
                <span className="flex items-center gap-1">
                  Stage <SortIcon col="lifecycle_stage" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('contract_status')}>
                <span className="flex items-center gap-1">
                  Contract <SortIcon col="contract_status" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('dd_status')}>
                <span className="flex items-center gap-1">
                  Due Diligence <SortIcon col="dd_status" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('insurance_status')}>
                <span className="flex items-center gap-1">
                  Insurance <SortIcon col="insurance_status" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('next_review')}>
                <span className="flex items-center gap-1">
                  Next Review <SortIcon col="next_review" />
                </span>
              </th>
              <th className={thCls} onClick={() => handleSort('overall_status')}>
                <span className="flex items-center gap-1">
                  Overall <SortIcon col="overall_status" />
                </span>
              </th>
              <th className="px-3 py-2 w-16 text-[hsl(var(--text-muted))] font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--border))]">
            {paginated.map((vendor) => (
              <tr
                key={vendor.id}
                className={`table-row-hover transition-colors group ${
                  selectedIds.has(vendor.id) ? 'bg-teal-50/60 dark:bg-teal-950/10' : ''
                }`}
              >
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => toggleSelect(vendor.id)}
                    className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
                  >
                    {selectedIds.has(vendor.id) ? (
                      <CheckSquare size={14} className="text-teal-600" />
                    ) : (
                      <Square size={14} />
                    )}
                  </button>
                </td>
                <td className="px-3 py-2.5">
                  <div>
                    <button
                      onClick={() => setDetailVendor(vendor)}
                      className="font-medium text-[hsl(var(--text-primary))] hover:text-[hsl(var(--accent))] transition-colors text-left leading-snug"
                    >
                      {vendor.name}
                    </button>
                    {vendor.open_critical_issues > 0 && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-2xs text-red-600 font-medium">
                        <AlertTriangle size={9} />
                        {vendor.open_critical_issues} critical
                      </span>
                    )}
                    <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">
                      {vendor.department_owner}
                    </p>
                  </div>
                </td>
                <td className="px-3 py-2.5 font-mono text-[hsl(var(--text-muted))] tabular-nums whitespace-nowrap text-2xs">
                  {vendor.vendor_id}
                </td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-secondary))] whitespace-nowrap">
                  {vendor.category}
                </td>
                <td
                  className="px-3 py-2.5 text-[hsl(var(--text-secondary))] max-w-[130px] truncate"
                  title={vendor.department}
                >
                  {vendor.department}
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge type="risk" value={vendor.risk_tier} />
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge type="risk" value={vendor.criticality} />
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge type="lifecycle" value={vendor.lifecycle_stage} />
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge type="contract" value={vendor.contract_status} />
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge type="dd" value={vendor.dd_status} />
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge type="insurance" value={vendor.insurance_status} />
                </td>
                <td className="px-3 py-2.5 font-mono text-[hsl(var(--text-secondary))] tabular-nums whitespace-nowrap text-2xs">
                  {vendor.next_review || '—'}
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge type="overall" value={vendor.overall_status} />
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setDetailVendor(vendor)}
                      title="View vendor record"
                      className="p-1.5 rounded hover:bg-[hsl(var(--background))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] transition-colors"
                    >
                      <Eye size={13} />
                    </button>
                    <Link
                      href={`/vendor-workspace/${vendor.id}`}
                      title="Open full workspace"
                      className="p-1.5 rounded hover:bg-[hsl(var(--background))] text-[hsl(var(--text-muted))] hover:text-teal-600 transition-colors"
                    >
                      <ExternalLink size={13} />
                    </Link>
                    {can('vendor:edit') && (
                    <button
                      onClick={() =>
                        toast.info(`Edit: ${vendor.name}`, {
                          description: 'Vendor edit form — coming soon.',
                        })
                      }
                      title="Edit vendor"
                      className="p-1.5 rounded hover:bg-[hsl(var(--background))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] transition-colors"
                    >
                      <Edit3 size={13} />
                    </button>
                    )}
                    {can('vendor:flag') && (
                    <button
                      onClick={() =>
                        toast.warning(`Flagged: ${vendor.name}`, {
                          description: 'Vendor flagged for review.',
                        })
                      }
                      title="Flag for review"
                      className="p-1.5 rounded hover:bg-[hsl(var(--background))] text-[hsl(var(--text-muted))] hover:text-amber-600 transition-colors"
                    >
                      <Flag size={13} />
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr>
                <td colSpan={14} className="px-5 py-16 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Building2
                      size={28}
                      className="text-[hsl(var(--text-muted))] opacity-40"
                    />
                    <p className="text-sm font-medium text-[hsl(var(--text-secondary))]">
                      No vendors match your filters
                    </p>
                    <p className="text-xs text-[hsl(var(--text-muted))]">
                      Try adjusting your search or filter criteria.
                    </p>
                    <button onClick={clearFilters} className="btn-secondary mt-2 text-xs">
                      Clear All Filters
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-5 py-2.5 border-t border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[hsl(var(--text-muted))]">Rows per page:</span>
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            className="input-base text-xs h-7 w-auto cursor-pointer"
          >
            {[10, 25, 50].map((n) => (
              <option key={`pp-${n}`} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span className="text-xs text-[hsl(var(--text-muted))] tabular-nums">
            {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of{' '}
            {filtered.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className="btn-ghost text-xs h-7 px-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            «
          </button>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-ghost text-xs h-7 px-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ‹ Prev
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (page <= 3) {
              pageNum = i + 1;
            } else if (page >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = page - 2 + i;
            }
            return (
              <button
                key={`page-${pageNum}`}
                onClick={() => setPage(pageNum)}
                className={`text-xs h-7 w-7 rounded-md tabular-nums transition-colors ${
                  page === pageNum
                    ? 'bg-[hsl(var(--accent))] text-white font-semibold'
                    : 'btn-ghost'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || totalPages === 0}
            className="btn-ghost text-xs h-7 px-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next ›
          </button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages || totalPages === 0}
            className="btn-ghost text-xs h-7 px-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            »
          </button>
        </div>
      </div>

      {/* Vendor Detail Modal */}
      {detailVendor && (
        <VendorDetailModal vendor={dbToVendor(detailVendor)} onClose={() => setDetailVendor(null)} />
      )}
    </div>
  );
}